import { withQuery, type QueryValue } from '@/lib/routes/withQuery'

// Namespacing keeps call sites concise; this small helper object has negligible bundle impact.
export const inPersonVisitRoutes = {
    /**
     * In-person visit order detail page.
     * Example: /in-person-visit/orders/order_123
     */
    order(orderId: string): string {
        return `/in-person-visit/orders/${orderId}`
    },

    /**
     * Booking entry point for the follow-up video visit a clinical review requires. The
     * visit is provisioned by the review, so this joins the video flow at billing
     * selection rather than at SKU screening.
     * Example: /visit/visit_123/options
     */
    followUpVideoVisitBooking(followUpVideoVisitId: string): string {
        return `/visit/${followUpVideoVisitId}/options`
    },

    /**
     * Detail page for a booked follow-up video visit.
     * Example: /visits/visit_123
     */
    followUpVideoVisit(followUpVideoVisitId: string): string {
        return `/visits/${followUpVideoVisitId}`
    },

    /**
     * Checkout page for an in-person visit order.
     * Optional query params carry provider and pricing display context.
     * Example: /in-person-visit/orders/order_123/checkout?providerName=Dr.%20Smith
     */
    checkout(orderId: string, query?: CheckoutQuery): string {
        return withQuery(`${inPersonVisitRoutes.order(orderId)}/checkout`, query)
    },

    /**
     * Confirmation page shown after checkout and required post-checkout steps.
     * Example: /in-person-visit/orders/order_123/confirmation
     */
    confirmation(orderId: string): string {
        return `${inPersonVisitRoutes.order(orderId)}/confirmation`
    },

    /**
     * Health history review entry point for an in-person visit order.
     * Example: /in-person-visit/orders/order_123/health-history?review=1
     */
    healthHistory(orderId: string, query?: HealthHistoryQuery): string {
        return withQuery(`${inPersonVisitRoutes.order(orderId)}/health-history`, query)
    },

    /**
     * Manual-add page for a health history category within the in-person visit flow.
     * Example: /in-person-visit/orders/order_123/health-history/conditions/add
     */
    healthHistoryAdd(
        orderId: string,
        section: 'allergies' | 'conditions' | 'medications' | 'procedures',
    ): string {
        return `${inPersonVisitRoutes.healthHistory(orderId)}/${section}/add`
    },

    /**
     * Intake questionnaire route for an in-person visit order.
     * When questionNumber is omitted, returns the history route that redirects to the next required step.
     * Example: /in-person-visit/orders/order_123/history/1
     */
    history(orderId: string, questionNumber?: string | number): string {
        const basePath = `${inPersonVisitRoutes.order(orderId)}/history`
        return questionNumber === undefined ? basePath : `${basePath}/${questionNumber}`
    },

    /**
     * Post-checkout router page that decides whether the user goes to health history or confirmation.
     * Example: /in-person-visit/orders/order_123/post-checkout
     */
    postCheckout(orderId: string): string {
        return `${inPersonVisitRoutes.order(orderId)}/post-checkout`
    },

    /**
     * Preferences page for location, timing, network, and similar-provider choices.
     * Example: /in-person-visit/orders/order_123/preferences?mode=edit
     */
    preferences(orderId: string, query?: PreferencesQuery): string {
        return withQuery(`${inPersonVisitRoutes.order(orderId)}/preferences`, query)
    },

    /**
     * Fulfillment-offer selection host for an in-person visit order (FO funnel).
     * Example: /in-person-visit/orders/order_123/offers
     */
    offers(orderId: string): string {
        return `${inPersonVisitRoutes.order(orderId)}/offers`
    },

    /**
     * Provider search/list page for an in-person visit order.
     * When returnTo is order, choosing a provider returns to the order detail page instead of review.
     * Example: /in-person-visit/orders/order_123/providers?returnTo=order
     */
    providers(orderId: string, query?: ProvidersBrowseQuery): string {
        return withQuery(`${inPersonVisitRoutes.order(orderId)}/providers`, query)
    },

    /**
     * Provider detail page within the provider selection flow.
     * Example: /in-person-visit/orders/order_123/providers/provider_456?zip_code=02116
     */
    provider(orderId: string, providerId: string, query?: ProviderQuery): string {
        return withQuery(`${inPersonVisitRoutes.providers(orderId)}/${providerId}`, query)
    },

    /**
     * Review page shown before checkout where selected provider and preferences are confirmed.
     * Example: /in-person-visit/orders/order_123/review?providerId=provider_456
     */
    review(orderId: string, query?: ReviewQuery): string {
        return withQuery(`${inPersonVisitRoutes.order(orderId)}/review`, query)
    },

    /**
     * Persona verification page for the main identity verification step.
     * Example: /in-person-visit/orders/order_123/verification/home
     */
    verificationHome(orderId: string): string {
        return `${inPersonVisitRoutes.order(orderId)}/verification/home`
    },

    /**
     * Persona verification page used before importing health history.
     * Example: /in-person-visit/orders/order_123/verification/health-history
     */
    verificationHealthHistory(orderId: string): string {
        return `${inPersonVisitRoutes.order(orderId)}/verification/health-history`
    },

    /**
     * Identity verification explainer page that routes into Persona when needed.
     * Example: /in-person-visit/orders/order_123/verify-identity
     */
    verifyIdentity(orderId: string): string {
        return `${inPersonVisitRoutes.order(orderId)}/verify-identity`
    },
}

/**
 * Query params displayed on the checkout page after provider selection.
 */
type CheckoutQuery = {
    providerName?: QueryValue
}

/**
 * Query params for entering health history in review mode.
 */
type HealthHistoryQuery = {
    review?: '1' | null | undefined
}

/**
 * Query params for editing preferences or returning from provider selection.
 */
type PreferencesQuery = {
    mode?: 'edit' | null | undefined
    /** Where edit mode returns to on save/cancel. Defaults to the order detail page. */
    returnTo?: 'order' | 'checkout' | null | undefined
    providerId?: QueryValue
    providerName?: QueryValue
    providerAddress?: QueryValue
    insurancePrice?: QueryValue
}

/**
 * Query params for the provider browse list (search and filters).
 */
type ProvidersBrowseQuery = {
    returnTo?: 'order' | 'preferences' | 'review' | 'checkout' | null | undefined
    /** When returnTo is 'preferences', restores the preferences page mode on return. */
    prefMode?: 'edit' | null | undefined
    /** When returnTo is 'preferences', restores the preferences edit return target on return. */
    prefReturnTo?: 'order' | 'checkout' | null | undefined
}

/**
 * Query params for provider detail lookup context.
 */
type ProviderQuery = {
    zip_code?: QueryValue
    /** 1-based rank in the search results list when opened from browse. */
    rank?: QueryValue
    returnTo?: 'order' | 'preferences' | 'review' | 'checkout' | null | undefined
    /** When returnTo is 'preferences', restores the preferences page mode on return. */
    prefMode?: 'edit' | null | undefined
    /** When returnTo is 'preferences', restores the preferences edit return target on return. */
    prefReturnTo?: 'order' | 'checkout' | null | undefined
}

/**
 * Query params used to prefill the review page after a provider is selected.
 */
type ReviewQuery = {
    providerName?: QueryValue
    providerAddress?: QueryValue
}
